import React, { type FC } from "react";
import { type ISearchResultItemModel, type SearchResultTopicModel } from "~/feature/Search/generated-types";
import { Text as ChakraText, Heading, AspectRatio, Flex, Box } from "@chakra-ui/react";
import { mediaApi, useSitecoreContext } from "~/foundation/Jss";
import { imageSizes } from "../../_Base/Theme";
import { LazyImage } from "~/foundation/Components/Image/LazyImage";
import { MotionBox } from "~/foundation/Framer/MotionBox";
import SimpleImagePlaceholder from "../../Media/SimpleImagePlaceholder/SimpleImagePlaceholder";
import { fadeIn } from "../../_Base/Theme/styles/motions";
import { sizes } from "../../_Base/Theme/variables/sizes";
import { Link } from "../../_Base/Components/Link";
import { sc } from "../../_Base/Jss";
import { Text as ThemeTextStyling } from "../../_Base/Theme/components/Text.ts";
import { dateFormatter } from "~/foundation/Utils/dateFormatter";
import TagLink from "../../_Base/Components/Tags/TagLink.tsx";

export enum AspectRatioOption {
	default = "default",
	cover = "cover",
	auto = "auto"
}
interface StoryListItemProps extends Omit<ISearchResultItemModel, 'date'> {
	gridColumn?: string;
	aspectRatio?: AspectRatioOption;
	isExternal?: boolean;
	eventEndDate?: string | Date;
	eventCountry?: string;
	date?: string | Date;
}

const StoryListItem: FC<StoryListItemProps> = ({
	url,
	smallImage,
	title,
	tags,
	description,
	gridColumn,
	aspectRatio,
	isExternal,
	eventEndDate,
	eventCountry,
	date,
}) => {
	const { sitecoreContext } = useSitecoreContext();
	const isRtl = sitecoreContext.custom.settings.isRtl;

	const isValidDate = (dateValue: string | Date | undefined): boolean => {
		if (!dateValue) return false;
		const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
		return date.getFullYear() > 1900;
	};

	// They format countries and states like this "country name;state name"
	const getCountryLabel = (countryString: string | undefined): string => {
		if (!countryString) return '';
		const country = countryString as string;
		return country.split(';')[0].trim();
	};

	// Get img aspect ratio
	const imageW = Number(smallImage?.width) || 4;
	const imageH = Number(smallImage?.height) || 3;

	let ratio = 4 / 3;
	if (aspectRatio === AspectRatioOption.cover) {
		ratio = 3 / 4;
	} else if (aspectRatio === AspectRatioOption.auto) {
		// calc aspect ratio from dimensions, fallback 4 / 3
		ratio = imageW / imageH;
	}

	const remToPx = (remValue: string, base: number = 16): number => {
		const numericValue = parseFloat(remValue.replace('rem', '').trim());
		return numericValue * base;
	};

	const contentWrapperWidthPx = remToPx(String(sizes.contentWrapperWidthXL));

	const getSrcSet = () => {
		if (aspectRatio === AspectRatioOption.cover) {
			return imageSizes.review.md;
		} else if (aspectRatio === AspectRatioOption.auto) {
			const baseWidth = contentWrapperWidthPx / 4;
			const baseHeight = baseWidth / ratio;

			return {
				cw: baseWidth,
				ch: baseHeight
			};
		}
		return imageSizes.articleList.sm;
	};

	const srcSet = getSrcSet();

	return (
		<MotionBox
			as="li"
			listStyleType="none"
			variants={fadeIn}
			gridColumn={gridColumn || "auto"}
		>
			<Link
				to={url}
				target={isExternal ? "_blank" : undefined}
				variant="unstyled"
				display="block"
				minW="100%"
				maxW="100%"
				_focus={{ outlineOffset: "0.5rem" }}
			>
				<AspectRatio ratio={ratio} h={aspectRatio === AspectRatioOption.auto ? "400px" : "auto"} overflow="hidden">
					{smallImage?.url ? (
						<LazyImage
							alt={smallImage.alt}
							src={mediaApi.updateImageUrl(smallImage?.url, srcSet)}
							w="100%"
							h="100%"
							sx={{
								objectFit: aspectRatio === AspectRatioOption.auto ? "contain !important" : "cover",
								objectPosition: aspectRatio === AspectRatioOption.auto ? "bottom" : "center center",
							}}
						/>
					) : (
						<SimpleImagePlaceholder missingAltText={title || "result"} />
					)}
				</AspectRatio>

				{title &&
					<Heading size="desktopHeadline5" my="3">{title}</Heading>
				}

				{tags && tags?.length > 0 && (
					<Flex flexWrap="wrap" gap={3}>
						{tags?.map((tagItem: SearchResultTopicModel, i) => (
							<React.Fragment key={i}>
								{tagItem.id ? (
									<TagLink tag={tagItem}>
										<ChakraText as="span" variant="tag">
											{tagItem.title}
										</ChakraText>
									</TagLink>
								) : (
									<ChakraText variant="tag">
										{tagItem.title}
									</ChakraText>
								)}
							</React.Fragment>
						))}
					</Flex>
				)}

				{/*
					Eventdate and country is only on events, no need to check for content type
					Events don't have tags, so won't stack with the above.
				*/}
				{eventCountry && (
					<ChakraText variant="tag">
						{getCountryLabel(eventCountry)}
					</ChakraText>
				)}


				{(eventEndDate || date) && (
					<ChakraText mt="3">
						<time>
							{isValidDate(date) && dateFormatter(typeof date === 'string' ? new Date(date) : date!, isRtl)}
							{isValidDate(eventEndDate) && isValidDate(date) && " - "}
							{isValidDate(eventEndDate) && dateFormatter(typeof eventEndDate === 'string' ? new Date(eventEndDate) : eventEndDate!, isRtl)}
						</time>
					</ChakraText>
				)}

				{description &&
					<Box noOfLines={4} mt="4" sx={{ ...ThemeTextStyling().sizes?.p16 }}>
						<sc.RichText editable={false} field={{ value: description }} />
					</Box>
				}
			</Link>
		</MotionBox>
	);
};

export default StoryListItem;